import { Injectable } from '@angular/core';
import { ERROR_CODES } from '../constants/fhir.constants';
import type { ConversionError } from '../interfaces/fhir-converter.interfaces';

/** Valid FHIR R4 AdministrativeGender values. */
const VALID_GENDERS = ['male', 'female', 'other', 'unknown'] as const;

/** Valid FHIR R4 Patient link types. */
const VALID_LINK_TYPES = ['replaced-by', 'replaces', 'refer', 'seealso'] as const;

/**
 * FHIR R4 Patient Validator
 *
 * Implements FHIR R4 Patient constraints:
 *
 * - **pat-1** (Rule on contact): SHALL at least contain a contact's details or a reference to an organization
 * - Gender value-set conformance (`male | female | other | unknown`)
 * - Link sub-element required fields: `other` (1..1), `type` (1..1)
 * - Communication sub-element required fields: `language` (1..1)
 */
@Injectable({ providedIn: 'root' })
export class FhirPatientValidator {
  validate(resource: unknown, errors: ConversionError[] = [], warnings: string[] = []): boolean {
    const r = resource as Record<string, unknown>;

    // Gender value-set
    this.validateGender(r, errors);

    // pat-1: Contact validation
    this.validateContacts(r, errors);

    // Link sub-element validation
    this.validateLinks(r, errors);

    // Communication sub-element validation
    this.validateCommunications(r, errors);

    // Informational warnings
    if (!r['name'] || (Array.isArray(r['name']) && (r['name'] as unknown[]).length === 0)) {
      warnings.push('Patient has no name specified');
    }

    return !errors.some((e) => e.severity === 'error');
  }

  private validateGender(r: Record<string, unknown>, errors: ConversionError[]): void {
    const gender = r['gender'] as string | undefined;
    if (gender && !VALID_GENDERS.includes(gender as (typeof VALID_GENDERS)[number])) {
      errors.push({
        code: ERROR_CODES.PAT_INVALID_GENDER,
        message: `Invalid gender "${gender}". Must be one of: ${VALID_GENDERS.join(', ')}`,
        path: 'gender',
        severity: 'error',
      });
    }
  }

  /** Pat-1: Each contact SHALL have at least name, telecom, address, or organization. */
  private validateContacts(r: Record<string, unknown>, errors: ConversionError[]): void {
    const contacts = r['contact'] as Record<string, unknown>[] | undefined;
    if (!contacts || !Array.isArray(contacts)) return;

    for (let i = 0; i < contacts.length; i++) {
      const c = contacts[i];
      const hasName = c['name'] !== undefined && c['name'] !== null;
      const hasTelecom = Array.isArray(c['telecom']) && (c['telecom'] as unknown[]).length > 0;
      const hasAddress = c['address'] !== undefined && c['address'] !== null;
      const hasOrganization = c['organization'] !== undefined && c['organization'] !== null;

      if (!hasName && !hasTelecom && !hasAddress && !hasOrganization) {
        errors.push({
          code: ERROR_CODES.PAT_1_CONTACT_DETAILS,
          message: `pat-1: contact[${i}] must have at least a name, telecom, address, or organization`,
          path: `contact[${i}]`,
          severity: 'error',
        });
      }
    }
  }

  /** Validate link sub-elements: other (1..1), type (1..1). */
  private validateLinks(r: Record<string, unknown>, errors: ConversionError[]): void {
    const links = r['link'] as Record<string, unknown>[] | undefined;
    if (!links || !Array.isArray(links)) return;

    for (let i = 0; i < links.length; i++) {
      const link = links[i];

      if (!link['other']) {
        errors.push({
          code: ERROR_CODES.PAT_LINK_MISSING_OTHER,
          message: `link[${i}].other is required`,
          path: `link[${i}].other`,
          severity: 'error',
        });
      }

      const type = link['type'] as string | undefined;
      if (!type) {
        errors.push({
          code: ERROR_CODES.PAT_LINK_MISSING_TYPE,
          message: `link[${i}].type is required`,
          path: `link[${i}].type`,
          severity: 'error',
        });
      } else if (!VALID_LINK_TYPES.includes(type as (typeof VALID_LINK_TYPES)[number])) {
        errors.push({
          code: ERROR_CODES.PAT_LINK_INVALID_TYPE,
          message: `link[${i}].type "${type}" is invalid. Must be one of: ${VALID_LINK_TYPES.join(', ')}`,
          path: `link[${i}].type`,
          severity: 'error',
        });
      }
    }
  }

  /** Validate communication sub-elements: language (1..1). */
  private validateCommunications(r: Record<string, unknown>, errors: ConversionError[]): void {
    const comms = r['communication'] as Record<string, unknown>[] | undefined;
    if (!comms || !Array.isArray(comms)) return;

    for (let i = 0; i < comms.length; i++) {
      if (!comms[i]['language']) {
        errors.push({
          code: ERROR_CODES.PAT_COMMUNICATION_MISSING_LANGUAGE,
          message: `communication[${i}].language is required`,
          path: `communication[${i}].language`,
          severity: 'error',
        });
      }
    }
  }
}
